import { Question } from "../types/Question";

/**
 * Champs utilisateur requis pour considérer le "profil" (infos d'affichage) comme complet.
 * Utilisé avec les données du token (AuthContext) ou GET /v2/users.
 */
export type UserProfileFields = {
    firstname?: string | null;
    lastname?: string | null;
    pseudo?: string | null;
};

/**
 * Vérifie si les champs obligatoires du profil utilisateur sont renseignés
 * (non vides après trim). Champs obligatoires : firstname, lastname.
 * Le pseudo n'est plus demandé (ancienne donnée).
 */
export function isUserProfileComplete(user: UserProfileFields | null | undefined): boolean {
    if (!user) return false;
    const firstname = (user.firstname ?? "").trim();
    const lastname = (user.lastname ?? "").trim();
    return firstname !== "" && lastname !== "";
}

/**
 * Vérifie si toutes les questions compte ont une réponse non null et non vide.
 * Basé sur GET /v2/users/questions : answer null = non répondu.
 */
export function areQuestionsComplete(questions: Question[] | null | undefined): boolean {
    if (!questions || questions.length === 0) return true;
    return questions.every((q) => {
        const answer = q.answer;
        return answer != null && String(answer).trim() !== "";
    });
}

/**
 * Profil considéré comme complet si :
 * - Les champs user obligatoires (firstname, lastname) sont renseignés, ET
 * - Toutes les questions (GET /v2/users/questions) ont un answer non null et non vide.
 * Le pseudo n'est plus pris en compte. Réutilisable dans un hook, layout ou autre page.
 */
export function isProfileComplete(
    user: UserProfileFields | null | undefined,
    questions: Question[] | null | undefined
): boolean {
    return isUserProfileComplete(user) && areQuestionsComplete(questions);
}

export type MissingUserFieldKey = "firstname" | "lastname";

const USER_FIELD_LABELS: Record<MissingUserFieldKey, string> = {
    firstname: "Prénom",
    lastname: "Nom",
};

/**
 * Retourne les libellés des champs user manquants (non renseignés ou vides).
 * Le pseudo n'est plus pris en compte.
 */
export function getMissingUserFieldLabels(
    user: UserProfileFields | null | undefined
): string[] {
    if (!user) return [USER_FIELD_LABELS.firstname, USER_FIELD_LABELS.lastname];
    const keys: MissingUserFieldKey[] = ["firstname", "lastname"];
    return keys
        .filter((key) => !(user[key] ?? "").trim())
        .map((key) => USER_FIELD_LABELS[key]);
}

/**
 * Retourne le nombre de questions avec réponse et le total.
 */
export function getQuestionsProgress(questions: Question[] | null | undefined): {
    answered: number;
    total: number;
} {
    if (!questions || questions.length === 0)
        return { answered: 0, total: 0 };
    const answered = questions.filter(
        (q) => q.answer != null && String(q.answer).trim() !== ""
    ).length;
    return { answered, total: questions.length };
}

/**
 * Pourcentage de complétion (0–100) : 2 champs user (prénom, nom) + N questions.
 * Le pseudo n'est plus pris en compte.
 */
export function getCompletionPercent(
    user: UserProfileFields | null | undefined,
    questions: Question[] | null | undefined
): number {
    const userCompleted = [user?.firstname, user?.lastname].filter(
        (v) => (v ?? "").trim() !== ""
    ).length;
    const { answered, total } = getQuestionsProgress(questions);
    const totalSteps = 2 + total;
    if (totalSteps === 0) return 100;
    return Math.round(((userCompleted + answered) / totalSteps) * 100);
}
